import React, { FC, Suspense } from 'react';
import { MAINTENANCE_ROUTES, PERMISSIONS } from '@trip-ux/constants';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Progress } from '@trip-ux/components';
import { WithRole } from '../shared/WithRole';
import TMSProvider from '../shared/TMSProvider';
import { useSelector } from 'react-redux';
import { ReduxStore } from '@trip-ux/redux-store';
import { lazyWithChunkRetry } from '@trip-ux/common';

const PolicySearch = lazyWithChunkRetry(() => import('./search/PolicySearch'));
const AssociatedPolicies = lazyWithChunkRetry(() => import('./endorsement/associated-policies/AssociatedPolicies'));
const EndorsePolicies = lazyWithChunkRetry(() => import('./endorsement/endorse-policies/EndorsePolicies'));
const FinancialTransaction = lazyWithChunkRetry(() => import('./endorsement/financial/FinancialTransaction'));
const Delivery = lazyWithChunkRetry(() => import('./endorsement/delivery/Delivery'));
const PolicyNotes = lazyWithChunkRetry(() => import('./endorsement/policy-notes/PolicyNotes'));
export interface MaintenanceProps {
  // No props needed since basename is handled by parent Router
}

const RoleAndTranslationProvider = ({ roles, children }) => (
  <WithRole.Route roles={roles}>
    <TMSProvider>{children}</TMSProvider>
  </WithRole.Route>
);

const EndorsementRedirect: FC = () => {
  // Navigate to policies tab using relative navigation
  return <Navigate to="policies" replace />;
};

export const MaintenanceRoutes: FC<MaintenanceProps> = () => {
  const { locale } = useSelector((store: ReduxStore) => ({
    locale: store.config.locale
  }));
  
  return (
    <Suspense fallback={<Progress />}>
      <Routes>
        {/* Nested endorsement routes. Parent handles the :policyNumber param */}
        <Route path={`${MAINTENANCE_ROUTES[locale].ENDORSEMENT}/*`}>
          <Route index element={<EndorsementRedirect />} />
          <Route path="policies" element={
            <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_ASSOCIATED_POLICY}>
              <AssociatedPolicies />
            </RoleAndTranslationProvider>
          } />
          <Route path="view" element={
            <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_ASSOCIATED_POLICY}>
              <EndorsePolicies />
            </RoleAndTranslationProvider>
          } />
          <Route path="edit" element={
            <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_ASSOCIATED_POLICY}>
              <EndorsePolicies />
            </RoleAndTranslationProvider>
          } />
          <Route path="delivery" element={
            <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_DELIVERY}>
              <Delivery />
            </RoleAndTranslationProvider>
          } />
          <Route path="financials" element={
            <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_FINANCIAL_TRANSACTIONS}>
              <FinancialTransaction />
            </RoleAndTranslationProvider>
          } />
          <Route path="notes" element={
            <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_POLICY_NOTES}>
              <PolicyNotes />
            </RoleAndTranslationProvider>
          } />
        </Route>

        <Route path={MAINTENANCE_ROUTES[locale].SEARCH} element={
          <RoleAndTranslationProvider roles={PERMISSIONS.POLICY_MAINTENANCE.VIEW_ALL_POLICIES}>
            <PolicySearch />
          </RoleAndTranslationProvider>
        } />
      </Routes>
    </Suspense>
  );
};

MaintenanceRoutes.displayName = 'MaintenanceRoutes';
export default MaintenanceRoutes;
